/**
 * First-touch campaign attribution.
 *
 * Every Meta ad link already carries utm_* tags (HubSpot's ads integration
 * stamps them onto the creatives), but those params only exist on the LANDING
 * url. Each lead form posts `location.href` at SUBMIT time, which is nearly
 * always a later page, so the campaign was thrown away before anything read it
 * — 13 of 1,481 leads carried a campaign, and Norwegian lead-gen carried none
 * at all despite all seven of its ads being tagged.
 *
 * So capture the params once, on the page that actually has them, and keep them
 * for the rest of the session. First touch wins: the campaign that brought the
 * visitor is the one that earned the lead, not whatever they clicked later.
 *
 * sessionStorage, not localStorage, on purpose — this is the attribution window
 * for THIS visit. A month-old campaign resurfacing on a direct visit would be
 * worse than no answer, and a session-scoped tracking key is the proportionate
 * amount of data to keep.
 */
(function () {
  var KEY = 'slx_attribution';

  // utm_* is the campaign tagging; the click ids let a later fix reconcile
  // against Meta/Google reporting without another round of theme changes.
  var FIELDS = [
    'utm_campaign',
    'utm_source',
    'utm_medium',
    'utm_content',
    'utm_term',
    'utm_id',
    'fbclid',
    'gclid'
  ];

  // HubSpot's lead_campaign property is a 200-char string; anything longer
  // would fail the whole contact update, not just this field — every server
  // path that forwards these to HubSpot caps them at 200 again. The fbclid is
  // the exception: Meta rejects a modified (cut) fbclid when it is rebuilt into
  // fbc server-side, so it is kept whole up to 500 chars.
  function capValue(field, value) {
    return value.slice(0, field === 'fbclid' ? 500 : 200);
  }

  function read() {
    try {
      return JSON.parse(sessionStorage.getItem(KEY) || '{}') || {};
    } catch (e) {
      return {};
    }
  }

  function capture() {
    var stored = read();

    // First touch wins, so never overwrite a campaign already held this session.
    if (stored.utm_campaign) return stored;

    var params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (e) {
      return stored;
    }

    var found = {};
    var any = false;
    for (var i = 0; i < FIELDS.length; i++) {
      var value = (params.get(FIELDS[i]) || '').trim();
      if (!value) continue;
      found[FIELDS[i]] = capValue(FIELDS[i], value);
      any = true;
    }
    if (!any) return stored;

    try {
      sessionStorage.setItem(KEY, JSON.stringify(found));
    } catch (e) {
      // Private mode or storage disabled — fall through and return what we
      // parsed, so at least a lead submitted on this page is still attributed.
    }
    return found;
  }

  var captured = capture();

  /*
   * Long-lived first/last touch (`slx_attr`, localStorage, 90 days).
   *
   * The session key above answers "which campaign brought THIS visit". It
   * cannot answer "which campaign brought this person" for the visitor who
   * clicks an ad, leaves, and phones or emails a week later — and phone and
   * email are where many of the high-value leads end up. So keep a separate,
   * deliberately small record: the first touch (written once), the latest
   * campaign touch, and an expiry. No personal data — campaign tags, the
   * landing path and the referring host only.
   *
   * A first visit without utm still records a landing-only touch, so a lead
   * that started on /pages/angebot or /pages/salg is attributable to that page.
   */
  var ATTR_KEY = 'slx_attr';
  var ATTR_TTL = 90 * 864e5;
  var TOUCH_FIELDS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid', 'fbclid'];

  function loadAttr() {
    try {
      var s = JSON.parse(localStorage.getItem(ATTR_KEY) || 'null');
      if (s && s.first && s.exp > Date.now()) return s;
    } catch (e) {}
    return null;
  }

  function referrerHost() {
    try {
      var host = document.referrer ? new URL(document.referrer).hostname : '';
      // Own domains (and the other market's) are navigation, not a source.
      if (!host || host === location.hostname || /(^|\.)screenlux\.(de|no)$/.test(host)) return '';
      return host;
    } catch (e) {
      return '';
    }
  }

  var attrMem = (function () {
    var params;
    try { params = new URLSearchParams(location.search); } catch (e) { params = null; }
    var touch = {};
    var tagged = false;
    for (var i = 0; i < TOUCH_FIELDS.length; i++) {
      var v = params ? (params.get(TOUCH_FIELDS[i]) || '').trim() : '';
      if (v) tagged = true;
      touch[TOUCH_FIELDS[i]] = v ? capValue(TOUCH_FIELDS[i], v) : null;
    }
    touch.landing = (location.pathname + location.search).slice(0, 300);
    touch.referrer = referrerHost() || null;
    touch.ts = new Date().toISOString();

    var s = loadAttr();
    if (!s) s = { first: touch, last: touch, exp: Date.now() + ATTR_TTL };
    else if (tagged) s.last = touch;
    else return s; // nothing new — leave storage untouched
    try { localStorage.setItem(ATTR_KEY, JSON.stringify(s)); } catch (e) {}
    return s;
  })();

  /** The stored {first, last} (re-read so another tab's newer touch counts), or null. */
  function attr() {
    var s = loadAttr() || attrMem;
    return s && s.first ? { first: s.first, last: s.last || null } : null;
  }

  /*
   * tel:/mailto: click attribution.
   *
   * A click on a phone number or email address is a lead we otherwise never
   * see. Report it to Powerups with the stored touches, fire-and-forget via
   * sendBeacon so the dialer / mail client opens without any delay. For mailto
   * a short reference is put into the subject and sent with the event, so the
   * email that arrives can be matched back to the click (and its campaign).
   */
  var CLICK_URL = '/apps/measurements/contact-click';
  var REF_CHARS = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';

  function market() {
    var lang = (document.documentElement.lang || '').toLowerCase();
    return /^(nb|nn|no)\b/.test(lang) || /\.no$/.test(location.hostname) ? 'NO' : 'DE';
  }

  function newRef() {
    var out = 'R';
    var n = new Uint8Array(4);
    try { crypto.getRandomValues(n); } catch (e) { for (var j = 0; j < 4; j++) n[j] = Math.random() * 256; }
    for (var i = 0; i < 4; i++) out += REF_CHARS[n[i] % 32];
    return out;
  }

  function dec(v) {
    try { return decodeURIComponent(v); } catch (e) { return v; }
  }

  /** mailto href with " (Ref X)" appended to the subject; other params kept verbatim. */
  function withRef(href, ref, fallback) {
    var q = href.indexOf('?');
    var parts = q < 0 ? [] : href.slice(q + 1).split('&').filter(Boolean);
    var tag = ' (Ref ' + ref + ')';
    var done = false;
    parts = parts.map(function (p) {
      var eq = p.indexOf('=');
      if (done || (eq < 0 ? p : p.slice(0, eq)).toLowerCase() !== 'subject') return p;
      done = true;
      return 'subject=' + encodeURIComponent((dec(eq < 0 ? '' : p.slice(eq + 1)).trim() || fallback) + tag);
    });
    if (!done) parts.unshift('subject=' + encodeURIComponent(fallback + tag));
    return (q < 0 ? href : href.slice(0, q)) + '?' + parts.join('&');
  }

  function send(body) {
    var json = JSON.stringify(body);
    try { if (navigator.sendBeacon && navigator.sendBeacon(CLICK_URL, json)) return; } catch (e) {}
    try { fetch(CLICK_URL, { method: 'POST', body: json, keepalive: true }).catch(function () {}); } catch (e) {}
  }

  function contactLink(e) {
    // composedPath reaches links inside open shadow roots (widgets); closest
    // covers older browsers.
    var path = e.composedPath ? e.composedPath() : [];
    for (var i = 0; i < path.length; i++) {
      if (path[i].tagName === 'A' && /^(tel|mailto):/i.test(path[i].getAttribute('href') || '')) return path[i];
    }
    var t = e.target;
    return t && t.closest ? t.closest('a[href^="tel:" i],a[href^="mailto:" i]') : null;
  }

  document.addEventListener('click', function (e) {
    try {
      var a = contactLink(e);
      if (!a) return;
      // Rebuild from the original href every click, so each click gets
      // exactly one fresh ref rather than stacking them.
      var orig = a.getAttribute('data-slx-href') || a.getAttribute('href');
      var mkt = market();
      var body = { v: 1, kind: /^tel:/i.test(orig) ? 'tel' : 'mailto', market: mkt };
      var target = dec(orig.slice(body.kind.length + 1).split('?')[0]);
      if (body.kind === 'tel') {
        body.href_target = target.replace(/\D/g, '');
      } else {
        body.href_target = target.trim();
        body.ref = newRef();
        a.setAttribute('data-slx-href', orig);
        a.setAttribute('href', withRef(orig, body.ref, mkt === 'NO' ? 'Henvendelse' : 'Anfrage'));
      }
      body.page = location.pathname;
      body.ts = new Date().toISOString();
      body.attr = attr();
      send(body);
    } catch (err) {}
  }, true);

  window.slxAttribution = {
    /** The params for this session, or {} when the visit carried none. */
    get: function () {
      var stored = read();
      return stored.utm_campaign ? stored : captured;
    },
    /** Long-lived {first, last} touches (90 days), or null. */
    attr: attr
  };
})();
